import React, { createContext, useContext, useState, useEffect } from 'react'
import { onAuthStateChanged } from 'firebase/auth'
import { doc, getDoc } from 'firebase/firestore'
import { auth, db } from '../lib/firebase'

const AuthContext = createContext()

export const useAuth = () => {
    const context = useContext(AuthContext)
    if (!context) {
        throw new Error('useAuth must be used within AuthProvider')
    }
    return context
}

export const AuthProvider = ({ children }) => {
    const [currentUser, setCurrentUser] = useState(null)
    const [userRole, setUserRole] = useState(null)
    const [userRoleData, setUserRoleData] = useState(null)
    const [loading, setLoading] = useState(true)

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (user) => {
            if (user) {
                // SECURITY: Validate user is not admin/staff before updating context
                // This prevents admin session from leaking to customer site
                try {
                    const adminDoc = await getDoc(doc(db, 'admins', user.uid))

                    // If user is admin/staff and currently on customer site
                    // Don't update the context to prevent session leak
                    if (adminDoc.exists()) {
                        // This is an admin trying to use customer site
                        // Block context update completely - treat as not logged in
                        console.warn('Admin account detected on customer site, blocking context update')
                        setCurrentUser(null)
                        setUserRole(null)
                        setUserRoleData(null)
                        setLoading(false)
                        return
                    }

                    // User is valid customer, proceed normally
                    // Only load role data from users collection
                    const userDoc = await getDoc(doc(db, 'users', user.uid))

                    if (userDoc.exists()) {
                        setCurrentUser(user)
                        setUserRole('customer')
                        setUserRoleData(userDoc.data())
                    } else {
                        // User not in customer database - treat as not logged in
                        console.warn('User not found in customer database')
                        setCurrentUser(null)
                        setUserRole(null)
                        setUserRoleData(null)
                    }
                } catch (error) {
                    console.error('Error validating user:', error)
                    // SECURITY: Fail closed - don't allow through on error
                    // This prevents admin session leak if validation fails
                    setCurrentUser(null)
                    setUserRole(null)
                    setUserRoleData(null)
                }
            } else {
                // No user logged in
                setCurrentUser(null)
                setUserRole(null)
                setUserRoleData(null)
            }

            setLoading(false)
        })

        return unsubscribe
    }, [])

    const value = {
        currentUser,
        userRole, // 'admin', 'manager', or 'customer'
        userRoleData, // Full user/admin data
        loading,
        isAdmin: userRole === 'admin',
        isManager: userRole === 'manager',
        isCustomer: userRole === 'customer',
        isStaff: userRole === 'admin' || userRole === 'manager'
    }

    return (
        <AuthContext.Provider value={value}>
            {!loading && children}
        </AuthContext.Provider>
    )
}

export default AuthContext

